import React, { useState, useEffect } from 'react';
import axios from 'axios';

const TRIGGER_LABELS = {
  birthday: 'Doğum Günü Kutlaması',
  no_visit: 'Uzun Süredir Gelmeyenler',
  package_ending: 'Paketi Biten Müşteriler',
  appointment_24h: 'Randevu Hatırlatma (24s)',
  appointment_2h: 'Randevu Hatırlatma (2s)',
  no_show: 'Randevuya Gelmedi Takibi',
  lead_tag: 'Etikete Göre Takip',
  pre_consultation_no_response: 'Ön Görüşme - Cevap Vermeyenler',
  callback_request: 'Geri Arama Talepleri',
  appointment_before: 'Randevu Hatırlatma (Sesli)'
};

const TRIGGER_COLORS = {
  birthday: { color: '#fbbf24', bg: 'rgba(251, 191, 36, 0.15)' },
  no_visit: { color: '#f97316', bg: 'rgba(249, 115, 22, 0.15)' },
  package_ending: { color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.15)' },
  appointment_24h: { color: '#10b981', bg: 'rgba(16, 185, 129, 0.15)' },
  appointment_2h: { color: '#10b981', bg: 'rgba(16, 185, 129, 0.15)' },
  no_show: { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.15)' },
  lead_tag: { color: '#a855f7', bg: 'rgba(168, 85, 247, 0.15)' },
  pre_consultation_no_response: { color: '#0ea5e9', bg: 'rgba(14, 165, 233, 0.15)' },
  callback_request: { color: '#14b8a6', bg: 'rgba(20, 184, 166, 0.15)' },
  appointment_before: { color: '#10b981', bg: 'rgba(16, 185, 129, 0.15)' }
};

// Otomasyon kuralı oluşturma formunda seçilebilecek senaryolar - her biri
// automation_rules.trigger_type'a karşılık gelir. Sadece 'lead_tag' bir AI
// kategorisi gerektirir (diğerleri kendi kaynak tablosunun status'una göre
// çalışır - bkz. server/utils/automationRuleMatcher.js).
const SCENARIO_DEFS = [
  {
    type: 'birthday', icon: '🎂', label: 'Doğum Günü Kutlaması', needsCategory: false, defaultDays: 0, minDays: 0,
    description: 'Doğum günü bu hafta olan müşterilere otomatik kutlama mesajı/araması yapar.'
  },
  {
    type: 'no_visit', icon: '📆', label: 'Uzun Süredir Gelmeyenler', needsCategory: false, defaultDays: 45, minDays: 1,
    description: 'Son ziyaretinin üzerinden N gün geçmiş müşterileri hedefler.'
  },
  {
    type: 'package_ending', icon: '📦', label: 'Paketi Biten Müşteriler', needsCategory: false, defaultDays: 0, minDays: 0,
    description: 'Paketinde 1-2 seans kalan müşterileri otomatik hedefler.'
  },
  {
    type: 'lead_tag', icon: '🏷️', label: 'Etikete Göre Takip', needsCategory: true, defaultDays: 3, minDays: 1,
    description: 'AI, sohbeti kapatırken müşteriyi bir etiketle işaretler. Bu kural, o etiketten N gün sonra hâlâ dönüş yapmamış müşterilere AI\'nin kişiselleştirilmiş bir takip mesajı yazmasını sağlar.'
  },
  {
    type: 'pre_consultation_no_response', icon: '📋', label: 'Ön Görüşme - Cevap Vermeyenler', needsCategory: false, defaultDays: 1, minDays: 0,
    description: 'Ön Görüşme listesinde hâlâ ulaşılamamış (hiç aranmamış veya arandı ama cevap alınamamış) talepleri N gün sonra otomatik hedefler.'
  },
  {
    type: 'callback_request', icon: '☎️', label: 'Geri Arama Talepleri', needsCategory: false, defaultDays: 0, minDays: 0,
    description: 'Geri Arama listesindeki bekleyen "beni arayın" taleplerini hedefler (0 gün = bekletmeden).'
  },
  {
    type: 'appointment_before', icon: '📅', label: 'Randevu Hatırlatma (Sesli)', needsCategory: false, defaultDays: 2, minDays: 1, unit: 'hours',
    description: 'Randevusuna N saat kalan müşterileri otomatik arayıp hatırlatma yapar - randevu tarihi/saati aramada AI\'ya otomatik iletilir, ayrıca yazmanıza gerek yok.'
  }
];

// server/routes/automations.js GET /due-rules ile birebir aynı liste - n8n bu
// trigger_type'lar için WhatsApp'ı OTOMATİK (kendi zamanında) tetikler. Listede
// olmayanlarda (pre_consultation_no_response/callback_request/appointment_before)
// WhatsApp sadece "Şimdi Çalıştır" ile elle çalışır.
const AUTO_WHATSAPP_TRIGGER_TYPES = ['birthday', 'no_visit', 'package_ending', 'lead_tag'];

const BellIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
    <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
  </svg>
);

function Automations() {
  const [showAiModal, setShowAiModal] = useState(false);
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seenCategories, setSeenCategories] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [creating, setCreating] = useState(false);
  const [ruleForm, setRuleForm] = useState({ trigger_type: 'lead_tag', name: '', category: '', trigger_value: 3, template: '', channel: 'whatsapp' });
  const [branch, setBranch] = useState(null);
  const [savingReminders, setSavingReminders] = useState(false);
  const [channelOptions, setChannelOptions] = useState({ whatsapp: true, voice: { configured: false } });
  const [runningRuleId, setRunningRuleId] = useState(null);
  const [logs, setLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);
  const [resendingLogId, setResendingLogId] = useState(null);

  const fetchLogs = async () => {
    try {
      const token = localStorage.getItem('crm_token');
      const res = await axios.get('/api/automations/logs?limit=20', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(res.data || []);
    } catch (err) {
      console.error('Fetch automation logs error:', err);
    }
  };

  useEffect(() => {
    fetchRules();
    fetchLogs();
    fetchLeadCategories();
    fetchBranch();
    fetchChannelOptions();
  }, []);

  const fetchChannelOptions = async () => {
    try {
      const token = localStorage.getItem('crm_token');
      const res = await axios.get('/api/automations/channel-options', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChannelOptions(res.data);
    } catch (err) {
      console.error('Channel options fetch error:', err);
    }
  };

  const fetchBranch = async () => {
    try {
      const token = localStorage.getItem('crm_token');
      const res = await axios.get('/api/branches', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBranch(res.data?.[0] || null);
    } catch (err) {
      console.error('Branch fetch error:', err);
    }
  };

  const toggleReminders = async () => {
    if (!branch) return;
    const next = !branch.reminders_enabled;
    setSavingReminders(true);
    try {
      const token = localStorage.getItem('crm_token');
      await axios.put(`/api/branches/${branch.id}/reminders`, { reminders_enabled: next }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBranch(prev => ({ ...prev, reminders_enabled: next ? 1 : 0 }));
    } catch (err) {
      console.error('Toggle reminders error:', err);
      alert('Kaydedilemedi, tekrar deneyin.');
    } finally {
      setSavingReminders(false);
    }
  };

  const fetchRules = async () => {
    try {
      const token = localStorage.getItem('crm_token');
      const res = await axios.get('/api/automations', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRules(res.data.rules || []);
    } catch (err) {
      console.error('Automations fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeadCategories = async () => {
    try {
      const token = localStorage.getItem('crm_token');
      const res = await axios.get('/api/automations/lead-categories', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSeenCategories(res.data.seen || []);
      setAllCategories(res.data.all || []);
    } catch (err) {
      console.error('Lead categories fetch error:', err);
    }
  };

  const categoryLabel = (slug) => allCategories.find(c => c.slug === slug)?.label || slug;
  const currentScenario = SCENARIO_DEFS.find(s => s.type === ruleForm.trigger_type) || SCENARIO_DEFS[0];

  // Senaryo değiştirilince kategori/gün sayısı sıfırlanır, kanal da o senaryo
  // için mantıklı varsayılana çekilir (yeni 2 senaryo asıl amacı sesli arama
  // olduğu için - configured değilse WhatsApp'ta kalır).
  const selectScenario = (type) => {
    const scenario = SCENARIO_DEFS.find(s => s.type === type);
    const preferVoice = type !== 'lead_tag' && channelOptions.voice?.configured;
    setRuleForm(prev => ({
      ...prev,
      trigger_type: type,
      category: '',
      trigger_value: scenario.defaultDays,
      channel: preferVoice ? 'voice' : 'whatsapp'
    }));
  };

  const closeRuleModal = () => {
    setShowRuleModal(false);
    setEditingRuleId(null);
    setRuleForm({ trigger_type: 'lead_tag', name: '', category: '', trigger_value: 3, template: '', channel: 'whatsapp' });
  };

  const handleOpenEdit = (rule) => {
    setEditingRuleId(rule.id);
    setRuleForm({
      trigger_type: rule.trigger_type,
      name: rule.name || '',
      category: rule.category || '',
      trigger_value: rule.trigger_value ?? 0,
      template: rule.template || '',
      channel: rule.channel || 'whatsapp'
    });
    setShowRuleModal(true);
  };

  const handleDeleteRule = async (rule) => {
    if (!window.confirm(`"${rule.name || TRIGGER_LABELS[rule.trigger_type] || rule.trigger_type}" kuralını silmek istediğinize emin misiniz?`)) return;
    try {
      const token = localStorage.getItem('crm_token');
      await axios.delete(`/api/automations/${rule.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchRules();
    } catch (err) {
      console.error('Rule delete error:', err);
      alert(err.response?.data?.error || 'Kural silinemedi.');
    }
  };

  const handleSaveRule = async (e) => {
    e.preventDefault();
    if (!ruleForm.name.trim()) return;
    if (currentScenario.needsCategory && !ruleForm.category) return;
    if (!currentScenario.needsCategory && !ruleForm.template.trim()) return;
    setCreating(true);
    try {
      const token = localStorage.getItem('crm_token');
      const payload = {
        name: ruleForm.name.trim(),
        trigger_type: ruleForm.trigger_type,
        category: currentScenario.needsCategory ? ruleForm.category : undefined,
        trigger_value: parseInt(ruleForm.trigger_value, 10) || 0,
        channel: ruleForm.channel,
        template: ruleForm.template.trim()
      };
      if (editingRuleId) {
        await axios.put(`/api/automations/${editingRuleId}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post('/api/automations', payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      closeRuleModal();
      fetchRules();
    } catch (err) {
      console.error('Rule save error:', err);
      alert(err.response?.data?.error || 'Kural kaydedilemedi.');
    } finally {
      setCreating(false);
    }
  };

  const handleToggle = async (rule) => {
    try {
      const token = localStorage.getItem('crm_token');
      const res = await axios.post(`/api/automations/${rule.id}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRules(prev => prev.map(r => r.id === rule.id ? { ...r, is_active: res.data.is_active } : r));
    } catch (err) {
      console.error('Automation toggle error:', err);
      alert('Durum değiştirilemedi.');
    }
  };

  // Otomasyonların WhatsApp gönderimi n8n'in dışarıdaki iş akışına bağlı
  // (kendi zamanında polluyor) - ama yeni Ön Görüşme/Geri Arama senaryoları
  // n8n'e tanıtılmadığı için, bu kural türlerinde WhatsApp kanalı sadece bu
  // "Şimdi Çalıştır" butonuyla elle tetiklenebiliyor (Sesli Asistan zaten
  // voiceAutomationWorker.js ile otomatik çalışıyor).
  const handleRunNow = async (rule) => {
    setRunningRuleId(rule.id);
    try {
      const token = localStorage.getItem('crm_token');
      const res = await axios.post('/api/automations/run-now', { rule_id: rule.id }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(res.data.message || 'Kural tetiklendi.');
      fetchRules();
      fetchLogs();
    } catch (err) {
      console.error('Run-now error:', err);
      alert(err.response?.data?.error || 'Çalıştırılamadı.');
    } finally {
      setRunningRuleId(null);
    }
  };

  const handleResendLog = async (log) => {
    setResendingLogId(log.id);
    try {
      const token = localStorage.getItem('crm_token');
      await axios.post(`/api/automations/logs/${log.id}/resend`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchLogs();
    } catch (err) {
      alert(err.response?.data?.error || 'Yeniden gönderilemedi.');
    } finally {
      setResendingLogId(null);
    }
  };

  if (loading) return <div className="loading-container"><div className="loading-spinner"></div></div>;

  return (
    <div className="automations-page" style={{ padding: '24px' }}>
      {/* ── HEADER ── */}
      <div className="page-header" style={{ marginBottom: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h1 className="page-title" style={{ fontSize: '24px', fontWeight: 'bold', color: '#1e293b', marginBottom: '8px' }}>
            İş Akışları
          </h1>
          <p className="page-subtitle" style={{ color: '#64748b', fontSize: '14px' }}>
            Otomatik AI süreçleri
          </p>
        </div>
        <button
          onClick={() => { setEditingRuleId(null); setRuleForm({ trigger_type: 'lead_tag', name: '', category: '', trigger_value: 3, template: '', channel: 'whatsapp' }); setShowRuleModal(true); }}
          style={{
            padding: '10px 20px',
            background: '#a855f7',
            color: '#fff',
            border: 'none',
            borderRadius: '10px',
            fontWeight: '600',
            fontSize: '14px',
            cursor: 'pointer'
          }}
        >
          + Yeni Otomasyon Kuralı
        </button>
      </div>

      {/* ── RANDEVU HATIRLATMALARI ── */}
      {/* NOT: bu, asagidaki "kurallar" listesinden (automation_rules) tamamen
          BAGIMSIZ bir sistem - her randevu icin otomatik calisan 24s/2s
          hatirlatmalari (reminderWorker.js) yonetir. Kullanicilar bu switch'i
          burada aramaya egilimli oldugu icin (asil kural listesiyle karistigi
          icin) bilercek ayri ve net etiketli bir kart olarak gosteriliyor. */}
      <div style={{
          background: '#ffffff',
          borderRadius: '16px',
          padding: '20px 24px',
          marginBottom: '24px',
          border: '1px solid #f1f5f9',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '16px',
          flexWrap: 'wrap'
        }}>
        <div>
          <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a', margin: '0 0 4px 0' }}>
            🔔 Randevu Hatırlatmaları
          </h3>
          <p style={{ color: '#94a3b8', fontSize: '13px', margin: 0 }}>
            Her randevu için otomatik gönderilen 24 saat ve 2 saat kala hatırlatma mesajları
            (aşağıdaki kural listesinden bağımsız çalışır — kapatırsanız hiç kuyruğa girmez).
          </p>
        </div>
        <div
          onClick={savingReminders || !branch ? undefined : toggleReminders}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '10px',
            cursor: savingReminders || !branch ? 'default' : 'pointer',
            opacity: savingReminders || !branch ? 0.6 : 1,
            flexShrink: 0
          }}
        >
          <span style={{ fontSize: '13px', fontWeight: 600, color: branch?.reminders_enabled ? '#10b981' : '#94a3b8' }}>
            {branch?.reminders_enabled ? 'Açık' : 'Kapalı'}
          </span>
          <div style={{
            width: '44px',
            height: '24px',
            borderRadius: '999px',
            background: branch?.reminders_enabled ? '#10b981' : '#e2e8f0',
            position: 'relative',
            transition: 'background 0.2s'
          }}>
            <div style={{
              position: 'absolute',
              top: '2px',
              left: branch?.reminders_enabled ? '22px' : '2px',
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              background: '#fff',
              boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
              transition: 'left 0.2s'
            }} />
          </div>
        </div>
      </div>

      {/* ── AI'NIN KULLANDIĞI ETİKETLER ── */}
      <div style={{
          background: '#ffffff',
          borderRadius: '16px',
          padding: '20px 24px',
          marginBottom: '24px',
          border: '1px solid #f1f5f9',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)'
        }}>
        {seenCategories.length === 0 ? (
          <>
            <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a', margin: '0 0 4px 0' }}>
              🏷️ AI'nin Kullandığı Kategoriler
            </h3>
            <p style={{ color: '#94a3b8', fontSize: '13px', margin: 0 }}>
              Henüz AI tarafından kategorilenmiş bir sohbet görülmedi - AI sohbetleri kapattıkça
              burada birikmeye başlayacak.
            </p>
          </>
        ) : (
          <>
            <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a', margin: '0 0 4px 0' }}>
              🏷️ AI'nin Kullandığı Kategoriler
            </h3>
            <p style={{ color: '#94a3b8', fontSize: '12px', margin: '0 0 14px 0' }}>
              AI, her sohbeti serbest etiketlerle işaretliyor, biz bunları GÜVENİLİR ve KÜÇÜK
              bir kategori kümesine indirdik (aynı niyetin onlarca farklı yazılışı yerine).
              "Yeni Takip Kuralı" oluştururken bu kategorilerden birini seçersiniz.
            </p>
            <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: '#94a3b8', fontSize: '11px', textTransform: 'uppercase' }}>
                    <th style={{ padding: '6px 8px', fontWeight: '600' }}>Kategori</th>
                    <th style={{ padding: '6px 8px', fontWeight: '600' }}>Kaç Kez</th>
                    <th style={{ padding: '6px 8px', fontWeight: '600' }}>Son Kullanıldığı Müşteri</th>
                    <th style={{ padding: '6px 8px', fontWeight: '600' }}>Son Tarih</th>
                  </tr>
                </thead>
                <tbody>
                  {seenCategories.map(c => (
                    <tr key={c.category} style={{ borderTop: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '8px' }}>
                        <span style={{ background: 'rgba(168, 85, 247, 0.12)', color: '#a855f7', padding: '3px 10px', borderRadius: '999px', fontWeight: '600' }}>
                          {categoryLabel(c.category)}
                        </span>
                      </td>
                      <td style={{ padding: '8px', color: '#334155' }}>{c.count}</td>
                      <td style={{ padding: '8px', color: '#334155' }}>{c.last_customer_name || '—'}</td>
                      <td style={{ padding: '8px', color: '#64748b' }}>
                        {c.last_used_at ? new Date(c.last_used_at).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* ── KART GRİDİ ── */}
      {rules.length === 0 ? (
        <p style={{ color: '#64748b', fontSize: '14px' }}>Henüz otomasyon kuralı tanımlanmamış.</p>
      ) : (
        <div className="workflows-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
          gap: '20px'
        }}>
          {rules.map(rule => {
            const style = TRIGGER_COLORS[rule.trigger_type] || { color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.15)' };
            const isActive = !!rule.is_active;
            return (
              <div key={rule.id} onClick={() => handleToggle(rule)} style={{
                background: '#ffffff',
                borderRadius: '16px',
                padding: '24px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)',
                border: '1px solid #f1f5f9',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                height: '100%',
                transition: 'transform 0.2s',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      background: style.bg,
                      color: style.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <BellIcon />
                    </div>
                    <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#0f172a', margin: 0 }}>
                      {rule.name || TRIGGER_LABELS[rule.trigger_type] || rule.trigger_type}
                    </h3>
                  </div>
                  {['lead_tag', 'pre_consultation_no_response', 'callback_request', 'appointment_before'].includes(rule.trigger_type) ? (
                    <div style={{ margin: '0 0 24px 0' }}>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                        {rule.trigger_type === 'lead_tag' && (
                          <span style={{ background: style.bg, color: style.color, padding: '3px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: '600' }}>
                            🏷️ {categoryLabel(rule.category)}
                          </span>
                        )}
                        <span style={{ background: '#f1f5f9', color: '#64748b', padding: '3px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: '600' }}>
                          {rule.trigger_type === 'appointment_before'
                            ? `${rule.trigger_value ?? 0} saat kala`
                            : `${rule.trigger_value ?? 0} gün sonra`}
                        </span>
                        <span style={{ background: '#f1f5f9', color: '#64748b', padding: '3px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: '600' }}>
                          {rule.channel === 'voice' ? '📞 Sesli Asistan' : '💬 WhatsApp'}
                        </span>
                      </div>
                      {rule.template && (
                        <p style={{ color: '#64748b', fontSize: '13px', lineHeight: '1.5', margin: 0 }}>
                          AI talimatı: {rule.template}
                        </p>
                      )}
                    </div>
                  ) : (
                    <p style={{ color: '#64748b', fontSize: '13px', lineHeight: '1.5', margin: '0 0 24px 0' }}>
                      {rule.template}
                    </p>
                  )}
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #f1f5f9', paddingTop: '16px', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{
                    background: isActive ? 'rgba(16, 185, 129, 0.1)' : 'rgba(100, 116, 139, 0.1)',
                    color: isActive ? '#10b981' : '#64748b',
                    padding: '4px 12px',
                    borderRadius: '999px',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    {isActive ? 'Aktif' : 'Pasif'}
                  </span>
                  <span style={{ color: '#94a3b8', fontSize: '12px' }}>
                    {rule.run_count || 0} çalıştırma
                  </span>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleOpenEdit(rule); }}
                      style={{ background: 'rgba(100,116,139,0.1)', color: '#64748b', border: 'none', borderRadius: '8px', padding: '6px 12px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                    >
                      ✏️ Düzenle
                    </button>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleDeleteRule(rule); }}
                      style={{ background: 'rgba(239,68,68,0.1)', color: '#ef4444', border: 'none', borderRadius: '8px', padding: '6px 12px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                    >
                      🗑️
                    </button>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleRunNow(rule); }}
                      disabled={runningRuleId === rule.id}
                      style={{ background: 'rgba(59,130,246,0.1)', color: '#3b82f6', border: 'none', borderRadius: '8px', padding: '6px 12px', fontSize: '12px', fontWeight: 600, cursor: runningRuleId === rule.id ? 'default' : 'pointer', opacity: runningRuleId === rule.id ? 0.6 : 1 }}
                    >
                      {runningRuleId === rule.id ? '⏳ Çalışıyor...' : '▶ Şimdi Çalıştır'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── SON ÇALIŞTIRMA KAYITLARI ── */}
      <div style={{ marginTop: '32px' }}>
        <button
          type="button"
          onClick={() => setShowLogs(v => !v)}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#334155', fontSize: '14px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}
        >
          {showLogs ? '▾' : '▸'} Son Çalıştırma Kayıtları
        </button>
        {showLogs && (
          <div style={{ marginTop: '12px', background: '#fff', borderRadius: '14px', border: '1px solid #f1f5f9', overflow: 'hidden' }}>
            {logs.length === 0 ? (
              <p style={{ color: '#64748b', fontSize: '13px', padding: '16px', margin: 0 }}>Henüz kayıt yok.</p>
            ) : logs.map(log => (
              <div key={log.id} style={{ padding: '12px 16px', borderBottom: '1px solid #f8fafc' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '13px', color: '#334155', fontWeight: 600 }}>
                    {log.rule_name || 'Kural'} — {log.customer_name || log.phone}
                  </span>
                  <span style={{
                    fontSize: '11px', fontWeight: 700, padding: '2px 10px', borderRadius: '999px',
                    background: log.status === 'sent' || log.status === 'delivered' ? 'rgba(16,185,129,0.12)' : log.status === 'failed' ? 'rgba(239,68,68,0.12)' : 'rgba(148,163,184,0.15)',
                    color: log.status === 'sent' || log.status === 'delivered' ? '#10b981' : log.status === 'failed' ? '#ef4444' : '#64748b'
                  }}>
                    {log.channel === 'voice' ? '📞 ' : '💬 '}{log.status}
                  </span>
                </div>
                <div style={{ fontSize: '11.5px', color: '#94a3b8', marginTop: '2px' }}>
                  {new Date(log.triggered_at).toLocaleString('tr-TR')}
                </div>
                {log.status === 'failed' && log.error_message && (
                  <div style={{ fontSize: '12px', color: '#ef4444', marginTop: '6px', background: 'rgba(239,68,68,0.06)', padding: '6px 10px', borderRadius: '8px' }}>
                    ⚠️ {log.error_message}
                  </div>
                )}
                {log.channel === 'whatsapp' && (log.status === 'pending' || log.status === 'failed') && (
                  <button
                    onClick={() => handleResendLog(log)}
                    disabled={resendingLogId === log.id}
                    style={{ marginTop: '8px', background: 'rgba(59,130,246,0.1)', color: '#3b82f6', border: 'none', borderRadius: '8px', padding: '4px 10px', fontSize: '11.5px', fontWeight: 600, cursor: resendingLogId === log.id ? 'default' : 'pointer', opacity: resendingLogId === log.id ? 0.6 : 1 }}
                  >
                    {resendingLogId === log.id ? '⏳ Gönderiliyor...' : '🔁 Yeniden Gönder'}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── FLOATING AI BUTTON ── */}
      <div 
        onClick={() => setShowAiModal(true)}
        style={{
          position: 'fixed',
          bottom: '30px',
          right: '30px',
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          background: '#0ea5e9',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 10px 15px -3px rgba(14, 165, 233, 0.4), 0 4px 6px -2px rgba(14, 165, 233, 0.2)',
          cursor: 'pointer',
          zIndex: 1000,
          transition: 'transform 0.2s'
        }}
        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
      >
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="11" width="18" height="10" rx="2"></rect>
          <circle cx="12" cy="5" r="2"></circle>
          <path d="M12 7v4"></path>
          <line x1="8" y1="16" x2="8" y2="16"></line>
          <line x1="16" y1="16" x2="16" y2="16"></line>
        </svg>
      </div>

      {/* ── YENİ OTOMASYON KURALI MODAL ── */}
      {showRuleModal && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(15, 23, 42, 0.7)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
          padding: '20px',
          overflowY: 'auto'
        }} onClick={closeRuleModal}>
          <form
            onSubmit={handleSaveRule}
            onClick={e => e.stopPropagation()}
            style={{
              background: '#ffffff',
              borderRadius: '24px',
              padding: '32px',
              width: '100%',
              maxWidth: '460px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              margin: 'auto'
            }}
          >
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#0f172a', marginBottom: '4px' }}>
              {currentScenario.icon} {editingRuleId ? 'Kuralı Düzenle' : 'Yeni Otomasyon Kuralı'}
            </h2>
            <p style={{ color: '#64748b', fontSize: '13px', lineHeight: '1.5', marginBottom: '16px' }}>
              {currentScenario.description}
            </p>

            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>Senaryo</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
              {SCENARIO_DEFS.map(s => (
                <label key={s.type} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', borderRadius: '10px', border: `1px solid ${ruleForm.trigger_type === s.type ? '#a855f7' : '#e2e8f0'}`, background: ruleForm.trigger_type === s.type ? 'rgba(168,85,247,0.08)' : '#fff', color: '#334155', cursor: 'pointer', fontSize: '13px' }}>
                  <input type="radio" name="scenarioType" checked={ruleForm.trigger_type === s.type} onChange={() => selectScenario(s.type)} />
                  {s.icon} {s.label}
                </label>
              ))}
            </div>

            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>Kural Adı</label>
            <input
              type="text"
              value={ruleForm.name}
              onChange={e => setRuleForm({ ...ruleForm, name: e.target.value })}
              placeholder="Örn. Fiyat Sonrası Takip"
              required
              style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '14px', marginBottom: '16px' }}
            />

            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>Gönderim Kanalı</label>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '4px' }}>
              <label style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', borderRadius: '10px', border: `1px solid ${ruleForm.channel === 'whatsapp' ? '#a855f7' : '#e2e8f0'}`, background: ruleForm.channel === 'whatsapp' ? 'rgba(168,85,247,0.08)' : '#fff', color: '#334155', cursor: 'pointer', fontSize: '13px' }}>
                <input type="radio" name="ruleChannel" checked={ruleForm.channel === 'whatsapp'} onChange={() => setRuleForm({ ...ruleForm, channel: 'whatsapp' })} />
                💬 WhatsApp
              </label>
              <label style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', borderRadius: '10px', border: `1px solid ${ruleForm.channel === 'voice' ? '#a855f7' : '#e2e8f0'}`, background: ruleForm.channel === 'voice' ? 'rgba(168,85,247,0.08)' : '#fff', color: '#334155', cursor: channelOptions.voice?.configured ? 'pointer' : 'not-allowed', opacity: channelOptions.voice?.configured ? 1 : 0.5, fontSize: '13px' }}>
                <input
                  type="radio"
                  name="ruleChannel"
                  checked={ruleForm.channel === 'voice'}
                  disabled={!channelOptions.voice?.configured}
                  onChange={() => setRuleForm({ ...ruleForm, channel: 'voice' })}
                />
                📞 Sesli Asistan
              </label>
            </div>
            {!channelOptions.voice?.configured && (
              <p style={{ color: '#94a3b8', fontSize: '11.5px', margin: '0 0 16px 0' }}>
                Telefonla arama entegrasyonu yok ({channelOptions.voice?.reason || 'yapılandırılmamış'}) — AI Merkezi'nden bir sesli agent kurun.
              </p>
            )}
            {channelOptions.voice?.configured && ruleForm.channel === 'voice' && (
              <p style={{ color: '#94a3b8', fontSize: '11.5px', margin: '0 0 16px 0' }}>
                Sesli Asistan seçilirse, AI'ya talimat metni arama sırasında konuşma amacı olarak kullanılır
                {currentScenario.type !== 'lead_tag' ? ' - bu senaryo otomatik olarak (siz hiç dokunmadan) düzenli aralıklarla çalışır.' : '.'}
              </p>
            )}
            {ruleForm.channel === 'whatsapp' && !AUTO_WHATSAPP_TRIGGER_TYPES.includes(currentScenario.type) && (
              <p style={{ color: '#94a3b8', fontSize: '11.5px', margin: '0 0 16px 0' }}>
                Bu senaryoda WhatsApp otomatik/zamanlanmış çalışmaz - kural listesindeki
                "▶ Şimdi Çalıştır" butonuyla elle tetiklenir.
              </p>
            )}

            {currentScenario.needsCategory && (
              <>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>Kategori</label>
                <select
                  value={ruleForm.category}
                  onChange={e => setRuleForm({ ...ruleForm, category: e.target.value })}
                  required
                  style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '14px', marginBottom: '4px', background: '#fff' }}
                >
                  <option value="" disabled>Bir kategori seçin...</option>
                  {allCategories.filter(c => c.slug !== 'other').map(c => (
                    <option key={c.slug} value={c.slug}>{c.label}</option>
                  ))}
                </select>
                <p style={{ color: '#94a3b8', fontSize: '12px', margin: '0 0 16px 0' }}>
                  AI, sohbetleri otomatik olarak bu kategorilerden birine ayırıyor - serbest metin
                  yazmanıza gerek yok.
                </p>
              </>
            )}

            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
              {currentScenario.unit === 'hours' ? 'Kaç Saat Kala' : 'Kaç Gün Sonra'}
            </label>
            <input
              type="number"
              min={currentScenario.minDays}
              max={currentScenario.unit === 'hours' ? '72' : '30'}
              value={ruleForm.trigger_value}
              onChange={e => setRuleForm({ ...ruleForm, trigger_value: e.target.value })}
              style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '14px', marginBottom: '16px' }}
            />
            {currentScenario.minDays === 0 && (
              <p style={{ color: '#94a3b8', fontSize: '11.5px', margin: '-12px 0 16px 0' }}>0 = bekletmeden hedefle.</p>
            )}

            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
              {currentScenario.needsCategory ? "AI'ya Talimat (opsiyonel)" : (ruleForm.channel === 'voice' ? 'Arama Amacı / AI Talimatı' : 'Mesaj Şablonu')}
            </label>
            <textarea
              value={ruleForm.template}
              onChange={e => setRuleForm({ ...ruleForm, template: e.target.value })}
              placeholder={currentScenario.needsCategory
                ? "Örn. Fiyat konusunda hâlâ düşünüyor olabilir, nazikçe hatırlat ve yardımcı olmak istediğini belirt."
                : "Örn. Ön görüşme talebine dönüş yapılamadı, müşteriyle görüşüp randevu oluşturmaya çalış."}
              rows={3}
              required={!currentScenario.needsCategory}
              style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '14px', marginBottom: '20px', resize: 'vertical', fontFamily: 'inherit' }}
            />

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="button"
                onClick={closeRuleModal}
                style={{ flex: 1, padding: '12px', background: '#f1f5f9', color: '#334155', border: 'none', borderRadius: '12px', fontWeight: '600', cursor: 'pointer' }}
              >
                Vazgeç
              </button>
              <button
                type="submit"
                disabled={creating}
                style={{ flex: 1, padding: '12px', background: '#a855f7', color: '#fff', border: 'none', borderRadius: '12px', fontWeight: '600', cursor: creating ? 'default' : 'pointer', opacity: creating ? 0.6 : 1 }}
              >
                {creating ? 'Kaydediliyor...' : (editingRuleId ? 'Kaydet' : 'Oluştur')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── AI MODAL ── */}
      {showAiModal && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(15, 23, 42, 0.7)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001
        }} onClick={() => setShowAiModal(false)}>
          <div style={{
            background: '#ffffff',
            borderRadius: '24px',
            padding: '32px',
            width: '100%',
            maxWidth: '400px',
            textAlign: 'center',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }} onClick={e => e.stopPropagation()}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: 'rgba(14, 165, 233, 0.1)',
              color: '#0ea5e9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px auto'
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="10" rx="2"></rect>
                <circle cx="12" cy="5" r="2"></circle>
                <path d="M12 7v4"></path>
                <line x1="8" y1="16" x2="8" y2="16"></line>
                <line x1="16" y1="16" x2="16" y2="16"></line>
              </svg>
            </div>
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#0f172a', marginBottom: '12px' }}>
              AI İş Akışı Asistanı
            </h2>
            <p style={{ color: '#64748b', fontSize: '14px', lineHeight: '1.5', marginBottom: '24px' }}>
              Yakında: Mevcut hasta verilerinizden yola çıkarak sizin için en uygun AI süreçlerini otomatik oluşturabileceksiniz.
            </p>
            <button 
              onClick={() => setShowAiModal(false)}
              style={{
                width: '100%',
                padding: '12px',
                background: '#0f172a',
                color: '#fff',
                border: 'none',
                borderRadius: '12px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Anladım
            </button>
          </div>
        </div>
      )}

    </div>
  );
}

export default Automations;
